import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { PagesPaginateModel } from "../../../models/Paginate/PagesPaginate";
import { PlansService } from "../../../services/Financial/Plans/PlansServices";
import { ToastSettings } from "../../../utils/ToastSettings";
import { PlansModel } from "../../../models/Financial/Plans/PlansModel";

interface ContextProps {
  params: any;
  setParams: Dispatch<SetStateAction<any>>;
  pages: PagesPaginateModel;
  plans: PlansModel[];
  setPages: Dispatch<SetStateAction<PagesPaginateModel>>;
  isLoading: boolean;
  isLoadingModal: boolean;
  error: string;
  articlePlatform: any[];
  contentArticles: any[];
  setError: Dispatch<SetStateAction<string>>;
  handleList: () => void;
  handleFetch: (data: any) => Promise<boolean>;
  handleDelete: (id: number) => Promise<boolean>;
  handleListArticlesPlatform: () => void;
  handleListContentArticles: () => void;
}

export const PlansContext = createContext<ContextProps>({} as ContextProps);

interface PlansProviderProps {
  children: ReactNode;
}

export const PlansProvider = ({ children }: PlansProviderProps) => {
  const [params, setParams] = useState<any>(null);
  const [pages, setPages] = useState<PagesPaginateModel>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingModal, setIsLoadingModal] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [plans, setPlans] = useState<PlansModel[]>([]);
  const [articlePlatform, setArticlePlatform] = useState<any[]>([]);
  const [contentArticles, setContentArticles] = useState<any[]>([]);

  const service = new PlansService();
  const location = useLocation();

  const handleList = useCallback(async () => {
    if (!params) {
      return false;
    }

    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.list(params);
      setIsLoading(false);

      if (!!_Error) {
        return setError(_Response?.message || _Error);
      }

      setPlans(_Response?.data?.items);
      setPages(_Response?.data?.pagination);
      return setError("");
    } catch (e) {
      setIsLoading(false);
      setError("Não foi possível carregar os planos do sistema.");
    }
  }, [params, location.pathname]);

  const handleFetch = async (data: any) => {
    try {
      setIsLoadingModal(true);
      const [_Response, _Error, _Code, _Errors] = await service.fetch(data);
      setIsLoadingModal(false);
      if (!!_Error) {
        ToastSettings(
          _Response?.message || _Error,
          "bottom-center",
          "error",
          () => {},
          _Errors,
        );
        return false;
      }

      setError("");
      return true;
    } catch (e) {
      setError("Houve um erro ao salvar plano.");
      setIsLoadingModal(false);
      return false;
    }
  };

  const handleDelete = async (id: number) => {
    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.delete(id);
      setIsLoading(false);

      if (!!_Error) {
        setError(_Response?.message || _Error);
        return false;
      }

      setError("");
      return true;
    } catch (e: any) {
      setIsLoading(false);
      setError(
        e.response?.data?.message || "Houve um erro ao deletar o plano.",
      );
      return false;
    }
  };

  const handleListArticlesPlatform = useCallback(async () => {
    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.listArticlesPlatform();
      setIsLoading(false);

      if (!!_Error) {
        return setError(_Response?.message || _Error);
      }

      setArticlePlatform(_Response?.data);
      return setError("");
    } catch (e) {
      setIsLoading(false);
      setError("Não foi possível carregar os planos do sistema.");
    }
  }, []);

  const handleListContentArticles = useCallback(async () => {
    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.listContentArticles();
      setIsLoading(false);

      if (!!_Error) {
        return setError(_Response?.message || _Error);
      }

      setContentArticles(_Response?.data);
      return setError("");
    } catch (e) {
      setIsLoading(false);
      setError("Não foi possível carregar os planos do sistema.");
    }
  }, []);

  useEffect(() => {
    !!error && toast.error(error);
  }, [error]);

  useEffect(() => {
    handleList().then();
  }, [handleList]);

  return (
    <PlansContext.Provider
      value={{
        params,
        plans,
        setParams,
        pages,
        setPages,
        isLoading,
        isLoadingModal,
        error,
        contentArticles,
        articlePlatform,
        setError,
        handleList,
        handleFetch,
        handleDelete,
        handleListArticlesPlatform,
        handleListContentArticles,
      }}
    >
      {children}
    </PlansContext.Provider>
  );
};

export const usePlans = () => useContext(PlansContext);
